/**
 * Admins — ใครเข้า dashboard ได้บ้าง + role (แทน env ADMIN_EMAILS)
 * owner เท่านั้นที่เพิ่ม/แก้/ลบได้ · คนอื่นดูอย่างเดียว
 *
 * ใช้ CrudManager กลาง — canCreate/canEdit ตามสิทธิ์ owner, badge role + "คุณ"
 */
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { AdminsApi, type AdminUserItem, type AdminRole } from '@/lib/admin-api';
import { useRole } from '@/lib/use-role';
import { Badge } from '@/components/ui/badge';

const ROLES: { value: AdminRole; label: string }[] = [
  { value: 'owner', label: 'owner — ทุกสิทธิ์ + จัดการ admin' },
  { value: 'admin', label: 'admin — แก้ page/token + webhook' },
  { value: 'operator', label: 'operator — แก้ ราคา/โปร/คำตอบ/รูป' },
  { value: 'viewer', label: 'viewer — ดูอย่างเดียว' },
];
const roleClass: Record<string, string> = {
  owner: 'text-purple-800 border-purple-300',
  admin: 'text-blue-800 border-blue-300',
  operator: 'text-emerald-800 border-emerald-300',
  viewer: 'text-slate-700 border-slate-300',
};

const FIELDS: CrudField[] = [
  { key: 'email', label: 'อีเมล (Google)', type: 'text', required: true, placeholder: 'name@gmail.com', disabledOnEdit: true, help: 'บัญชี Google ที่ใช้ login' },
  { key: 'name', label: 'ชื่อ', type: 'text', help: 'ชื่อแสดง (ไม่บังคับ)' },
  { key: 'role', label: 'สิทธิ์ (role)', type: 'select', required: true, options: ROLES, default: 'viewer', help: 'กำหนดว่าทำอะไรได้บ้าง' },
  { key: 'active', label: 'เปิดสิทธิ์', type: 'toggle', default: true, help: 'ปิด = ระงับชั่วคราว (ยังอยู่ใน DB)' },
  { key: 'notes', label: 'หมายเหตุ', type: 'text', help: 'บันทึกภายใน' },
];

export default function AdminsPage() {
  const { canOwn, email: myEmail } = useRole();
  return (
    <CrudManager<AdminUserItem>
      title="🛡️ ผู้ดูแล (Admins)"
      addLabel="เพิ่ม admin"
      help="กำหนดว่าใครเข้า dashboard ได้ + สิทธิ์แต่ละคน. owner เท่านั้นที่จัดการได้ · กันลบ owner คนสุดท้ายอัตโนมัติ"
      queryKey="admins"
      api={AdminsApi}
      fields={FIELDS}
      canCreate={canOwn}
      canEdit={canOwn}
      hardDelete
      searchKeys={['email', 'name', 'role']}
      sortRows={(a, b) => a.email.localeCompare(b.email)}
      rowTitle={(a) => <>{a.email}</>}
      rowBadges={(a) => (
        <>
          {a.email === myEmail && <Badge variant="outline" className="text-[10px] text-yellow-800 border-yellow-300">คุณ</Badge>}
          <Badge variant="outline" className={`text-[10px] ${roleClass[a.role] ?? ''}`}>{a.role}</Badge>
        </>
      )}
      rowSubtitle={(a) => (
        <>
          {a.name ? `${a.name} · ` : ''}
          {a.lastLoginAt ? `เข้าล่าสุด ${new Date(a.lastLoginAt).toLocaleDateString('th-TH')}` : 'ยังไม่เคยเข้า'}
          {a.createdByEmail ? ` · เพิ่มโดย ${a.createdByEmail}` : ''}
          {a.notes ? ` · ${a.notes}` : ''}
        </>
      )}
    />
  );
}
